/**
 * Сервис push-уведомлений для PWA
 * Интеграция с backend по контракту Web Push:
 *   GET  /api/push/config       — публичный VAPID-ключ + флаг enabled
 *   POST /api/push/subscribe    — сохранить/обновить подписку (endpoint — ключ уникальности, дублей не будет)
 *   POST /api/push/unsubscribe  — отписать устройство
 *
 * Работает только вне Telegram Mini App — там Push API недоступен в принципе.
 * Запрос разрешения (subscribePushNotifications) вызывать только из обработчика
 * клика — иначе Safari проигнорирует, а Chrome включит «тихую» блокировку.
 */

import apiService from './api'
import i18n from '../i18n'

/**
 * Проверить поддержку push-уведомлений в текущем окружении
 */
export const isPushSupported = () => {
  // Внутри Telegram Mini App Push API недоступен — молча выходим
  // (SW там тоже не регистрируется, см. main.jsx)
  if (apiService.hasTelegramInitData()) {
    return false
  }

  return 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
}

/**
 * Получить текущее разрешение на уведомления
 * @returns {string} 'granted' | 'denied' | 'default' | 'unsupported'
 */
export const getNotificationPermission = () => {
  if (!isPushSupported()) {
    return 'unsupported'
  }
  return Notification.permission
}

/**
 * Получить конфиг push с бэкенда (публичный ключ + флаг enabled)
 * @returns {Promise<{enabled: boolean, public_key: string}|null>}
 */
export const fetchPushConfig = async () => {
  try {
    const response = await fetch(apiService.getApiUrl('/api/push/config'), {
      headers: { 'Accept': 'application/json' },
    })

    if (!response.ok) {
      return null
    }

    const data = await response.json()
    return data?.push ?? null
  } catch (error) {
    console.error('❌ Ошибка получения push-конфига:', error)
    return null
  }
}

/**
 * Отправить подписку на бэкенд (сохранение/обновление, привязка к аккаунту)
 * Повторные вызовы безопасны: endpoint — ключ уникальности на бэкенде
 * @returns {Promise<object|null>} поле push из ответа или null при ошибке
 */
export const sendPushSubscription = async (subscription) => {
  try {
    const isPwa = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }

    // Авторизация необязательна: подписку можно сохранить до входа,
    // но с токеном она сразу привяжется к аккаунту (linked: true)
    const authContext = apiService.getAuthContext()
    if (authContext.isAuthorized && authContext.token) {
      headers['X-Telegram-Init-Data'] = authContext.token
    }

    const response = await fetch(apiService.getApiUrl('/api/push/subscribe'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ...subscription.toJSON(), // endpoint + keys.p256dh + keys.auth
        platform: detectPlatform(),
        is_pwa: isPwa,
        lang: i18n.language || 'ru',
      }),
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const data = await response.json()
    console.log('✅ Push-подписка отправлена на сервер:', data?.push)
    return data?.push ?? null
  } catch (error) {
    console.error('❌ Ошибка отправки push-подписки:', error)
    return null
  }
}

/**
 * Подписаться на push-уведомления (запрашивает разрешение)
 * Вызывать ТОЛЬКО из обработчика клика пользователя
 * @returns {Promise<PushSubscription|null>} подписка или null при ошибке/отказе
 */
export const subscribePushNotifications = async () => {
  if (!isPushSupported()) {
    console.warn('⚠️ Push-уведомления не поддерживаются')
    return null
  }

  try {
    const config = await fetchPushConfig()
    if (!config?.enabled || !config?.public_key) {
      console.warn('⚠️ Push-уведомления отключены на бэкенде')
      return null
    }

    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      console.log('❌ Разрешение на уведомления не получено')
      return null
    }

    const registration = await navigator.serviceWorker.ready

    // subscribe() с тем же ключом возвращает существующую подписку — дублей не будет
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // обязательное требование Chrome
      applicationServerKey: urlBase64ToUint8Array(config.public_key),
    })

    console.log('✅ Подписка на push создана:', subscription.endpoint)

    await sendPushSubscription(subscription)

    return subscription
  } catch (error) {
    console.error('❌ Ошибка подписки на push:', error)
    return null
  }
}

/**
 * Синхронизировать существующую подписку с бэкендом без запроса разрешения.
 * Вызывать при каждом старте приложения и сразу после авторизации
 * (повторный вызов привязывает анонимную подписку к аккаунту).
 */
export const syncPushSubscription = async () => {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    return
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const existing = await registration.pushManager.getSubscription()

    if (existing) {
      await sendPushSubscription(existing)
    }
  } catch (error) {
    console.error('❌ Ошибка синхронизации push-подписки:', error)
  }
}

/**
 * Отписаться от push-уведомлений
 */
export const unsubscribePushNotifications = async () => {
  if (!isPushSupported()) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      return true
    }

    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }

    const authContext = apiService.getAuthContext()
    if (authContext.isAuthorized && authContext.token) {
      headers['X-Telegram-Init-Data'] = authContext.token
    }

    await fetch(apiService.getApiUrl('/api/push/unsubscribe'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    })

    await subscription.unsubscribe()
    console.log('✅ Отписка от push выполнена')

    return true
  } catch (error) {
    console.error('❌ Ошибка отписки от push:', error)
    return false
  }
}

/**
 * Проверить, есть ли активная подписка
 */
export const hasPushSubscription = async () => {
  if (!isPushSupported()) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    return Boolean(subscription)
  } catch {
    return false
  }
}

// Определение платформы для аналитики на бэкенде
function detectPlatform() {
  const ua = navigator.userAgent

  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  if (/android/i.test(ua)) {
    return 'android'
  }
  if (/Windows|Macintosh|Linux/.test(ua)) {
    return 'desktop'
  }
  return 'other'
}

// Вспомогательная функция — конвертация base64url в Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}
